import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo, ThemeToggle } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const AuthFrame = ({ title, desc, children }) => (
  <div className="relative grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden overflow-hidden lg:block">
      <img src="/img/ai_portrait_sample.jpg" alt="AI ile üretilmiş portre" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-indigo-900/30" />
      <div className="absolute bottom-12 left-12 right-12 text-white">
        <div className="font-display text-3xl font-bold leading-tight">Fikrinizi profesyonel içeriğe dönüştürün.</div>
        <p className="mt-3 text-white/70">Görsel, banner, post, story, reklam ve video — tek stüdyoda.</p>
      </div>
    </div>
    <div className="flex flex-col px-5 py-6 sm:px-12">
      <div className="flex items-center justify-between"><Logo /><ThemeToggle /></div>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10 animate-fade-up">
        <h1 className="text-3xl font-bold">{title}</h1>
        {desc && <p className="mt-2 text-sm text-muted-foreground">{desc}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`${id}-input`} className="h-11 rounded-xl" {...p} /></div>
);

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f.identifier, f.password);
      nav(u.role === "ADMIN" ? "/admin" : u.status !== "ACTIVE" ? "/aktivasyon" : loc.state?.from || "/panel");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <AuthFrame title="Tekrar hoş geldiniz" desc="Hesabınıza giriş yapın.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="login-identifier" label="E-posta veya kullanıcı adı" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} required autoComplete="username" />
        <Field id="login-password" label="Şifre" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required autoComplete="current-password" />
        <div className="text-right text-sm"><Link to="/sifremi-unuttum" className="text-primary hover:underline" data-testid="forgot-password-link">Şifremi unuttum</Link></div>
        <Button type="submit" className="btn-glow h-11 w-full rounded-xl" disabled={busy} data-testid="login-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Giriş Yap</Button>
        <p className="text-center text-sm text-muted-foreground">Hesabınız yok mu? <Link to="/kayit" className="font-semibold text-primary" data-testid="go-register-link">Kayıt olun</Link></p>
      </form>
    </AuthFrame>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", accept_terms: false, whatsapp_transactional: false, whatsapp_marketing: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_terms) return toast.error("Lütfen sözleşmeleri onaylayın.");
    setBusy(true);
    try {
      await register({ ...f, phone: f.phone || null });
      toast.success("Hesabınız oluşturuldu. Aktivasyon için İlk Kayıt Paketini tamamlayın.");
      nav("/aktivasyon");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const Check = ({ k, children }) => (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
      <Checkbox checked={f[k]} onCheckedChange={(v) => setF({ ...f, [k]: !!v })} data-testid={`register-${k}-checkbox`} className="mt-0.5" /><span>{children}</span>
    </label>
  );
  return (
    <AuthFrame title="Hesap oluşturun" desc="Kayıt sonrası İlk Kayıt Paketi ile hesabınız aktif edilir.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <Field id="register-first-name" label="Ad" value={f.first_name} onChange={set("first_name")} required />
          <Field id="register-last-name" label="Soyad" value={f.last_name} onChange={set("last_name")} required />
        </div>
        <Field id="register-email" label="E-posta" type="email" value={f.email} onChange={set("email")} required autoComplete="email" />
        <Field id="register-phone" label="Telefon (opsiyonel)" placeholder="05xx xxx xx xx" value={f.phone} onChange={set("phone")} />
        <Field id="register-password" label="Şifre" type="password" value={f.password} onChange={set("password")} required minLength={8} autoComplete="new-password" />
        <p className="-mt-2 text-xs text-muted-foreground">En az 8 karakter, harf ve rakam içermeli.</p>
        <div className="space-y-3 rounded-xl border border-border p-4">
          <Check k="accept_terms"><Link to="/yasal/kullanici-sozlesmesi" target="_blank" className="text-primary">Kullanıcı Sözleşmesi</Link>'ni ve <Link to="/yasal/kvkk" target="_blank" className="text-primary">KVKK Aydınlatma Metni</Link>'ni okudum, kabul ediyorum.</Check>
          <Check k="whatsapp_transactional">WhatsApp üzerinden işlem bildirimleri almak istiyorum.</Check>
          <Check k="whatsapp_marketing">Kampanya ve pazarlama mesajları almak istiyorum (<Link to="/yasal/acik-riza" target="_blank" className="text-primary">Açık Rıza</Link>).</Check>
        </div>
        <Button type="submit" className="btn-glow h-11 w-full rounded-xl" disabled={busy} data-testid="register-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kayıt Ol</Button>
        <p className="text-center text-sm text-muted-foreground">Zaten hesabınız var mı? <Link to="/giris" className="font-semibold text-primary" data-testid="go-login-link">Giriş yapın</Link></p>
      </form>
    </AuthFrame>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthFrame title="Şifremi unuttum" desc="E-posta adresinizi girin, sıfırlama bağlantısı gönderelim.">
      {sent ? <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm" data-testid="forgot-success">Eğer bu e-posta kayıtlıysa, şifre sıfırlama bağlantısı gönderildi.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="forgot-email" label="E-posta" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Button type="submit" className="btn-glow h-11 w-full rounded-xl" data-testid="forgot-submit-button">Bağlantı Gönder</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/giris" className="text-primary">Girişe dön</Link></p>
    </AuthFrame>
  );
};

export const ResetPassword = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token") || "", password }); toast.success("Şifreniz güncellendi."); nav("/giris"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthFrame title="Yeni şifre belirleyin">
      <form onSubmit={submit} className="space-y-4">
        <Field id="reset-password" label="Yeni şifre" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
        <Button type="submit" className="btn-glow h-11 w-full rounded-xl" data-testid="reset-submit-button">Şifreyi Güncelle</Button>
      </form>
    </AuthFrame>
  );
};
